/**
 * This slice is to manage config fields
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LoadingStatus, SavingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { getClientService } from 'GeminiViewerComponent/features/services/client.service';
import { config } from 'gemini-config';

const clientService = getClientService({ config: config });

const initialState = {
    error: null,
    clients: [],
    client: null,
    loadingStatus: LoadingStatus.Idle,
    savingStatus: SavingStatus.Idle,
    pageInfo: {},
};

export const fetchClientById = createAsyncThunk('companionApps/fetchClientById', async (clientId) => {
    const response = await clientService.getById(clientId);
    return response;
});

export const fetchAllClients = createAsyncThunk('clients/fetchAllClients', async () => {
    return await clientService.getAllClients();
});

export const fetchAllClientsFiltered = createAsyncThunk('clients/fetchAllClientsFiltered', async (params) => {
    return await clientService.getAllClientsFiltered(params);
});

export const createClient = createAsyncThunk('clients/createClient', async (params) => {
    const response = await clientService.createClient(params);
    return response;
});

export const editClient = createAsyncThunk('clients/editClient', async (params) => {
    const response = await clientService.updateName(params.client_id, {
        display_name: params.display_name,
    });
    return response;
});

const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        resetClientsState: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllClients.pending, (state) => {
                state.loadingStatus = LoadingStatus.Loading;
                state.error = null;
            })
            .addCase(fetchAllClients.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                state.clients = action.payload;
            })
            .addCase(fetchAllClients.rejected, (state, action) => {
                state.loadingStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(fetchAllClientsFiltered.pending, (state) => {
                state.loadingStatus = LoadingStatus.Loading;
                state.error = null;
            })
            .addCase(fetchAllClientsFiltered.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                state.clients = action.payload.clients ?? [];
                state.pageInfo = action.payload.pageInfo;
            })
            .addCase(fetchAllClientsFiltered.rejected, (state, action) => {
                state.loadingStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(fetchClientById.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                state.client = action.payload;
            })
            .addCase(fetchClientById.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Idle;
            })
            .addCase(createClient.rejected, (state, action) => {
                state.error = action.error.message;
            })
            .addCase(editClient.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Idle;
            })
            .addCase(editClient.rejected, (state, action) => {
                state.error = action.error.message;
            });
    },
});

export const selectClientsErrorMessage = (state) => state.clients.error;
export const selectClientsLoadingStatus = (state) => state.clients.loadingStatus;
export const selectClients = (state) => {
    return state.clients.clients;
};
export const selectClientsFiltered = (state, filter) => {
    return filter ? state.clients.clients.filter(filter) : state.clients.clients;
};
export const selectClientsPageInfo = (state) => state.clients.pageInfo;

export const selectClient = (state) => state.clients.client;

export const { resetClientsState } = clientsSlice.actions;

export default clientsSlice.reducer;
